import React, { useRef, useState } from "react";

import {
    BottomSheet,
    Box,
    Button,
    Col,
    DataDisplay,
    Dropdown,
    DropdownItem,
    Grid,
    Icon,
    Separator,
    Text,
    useIsMobile,
} from "@ui-kit/components/index.es";
import ButtonDrawer from "pages/_components/drawer/DrawerModal";
import Notification from "pages/_components/Notification";
import SetAlias from "pages/accounts/_components/SetAlias";

import { actions as notificationActions } from "reducers/notification";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { selectors as sessionSelectors } from "reducers/session";

import * as i18nUtils from "util/i18n";
import { isMobileNative } from "util/device";
import { maskedAccountNumber, productTypes } from "util/accounts";
import { connect } from "react-redux";
import { arrayOf, bool, func, number as numberPropType, oneOfType, shape, string } from "prop-types";

function Information({
    dispatch,
    account: {
        productType,
        currency,
        number,
        cbu,
        cbuAlias,
        ownerName,
        documentNumber,
        productAlias,
        shortLabel,
        documentType,
    },
    credentialGroups,
    activeEnvironment,
    onClickDownloadInfo,
    fetchingDownload,
}) {
    const [isOpenDrawerEditAlias, setIsOpenDrawerEditAlias] = useState(false);
    const [isOpenDownloadOptionsMobile, setIsOpenDownloadOptionsMobile] = useState(false);
    const { isMobile, windowSize } = useIsMobile();
    const buttonRef = useRef(null);

    const accountType = i18nUtils.get(`accounts.productType.${productTypes[productType]}`).toUpperCase();
    const currencyType = i18nUtils.get(`currency.label.${currency}`);
    const accountLabel = `${accountType} en ${currencyType}`;

    const formatDocument = (document, docType) => {
        let documentFormated = "";
        const documentWithoutBlank = document.replace(" ", "").trim();
        if (docType !== "DNI" && documentWithoutBlank.length > 2) {
            if (documentWithoutBlank.length > 2) {
                documentFormated = documentWithoutBlank.substring(0, 2).concat("-");
            }
            if (documentWithoutBlank.length <= 10) {
                documentFormated += documentWithoutBlank.substring(2);
            }
            if (documentWithoutBlank.length > 10) {
                documentFormated += documentWithoutBlank
                    .substring(2, 10)
                    .concat("-")
                    .concat(documentWithoutBlank.substring(10));
            }
        } else {
            documentFormated = documentWithoutBlank;
        }

        return documentFormated;
    };

    const data = [
        {
            title: i18nUtils.get("accounts.information.accountType"),
            value: accountLabel || " - ",
        },
        {
            title: i18nUtils.get("accounts.information.accountNumber"),
            value: number || " - ",
        },
        {
            title: i18nUtils.get("accounts.information.cbu").toUpperCase(),
            value: cbu || " - ",
            canCopy: true,
            canShare: isMobileNative,
            onCopy: () =>
                dispatch(
                    notificationActions.showNotification(i18nUtils.get("accounts.information.data.copied"), "success", [
                        "account/details",
                    ]),
                ),
        },
        {
            title: i18nUtils.get("accounts.information.alias"),
            value: cbuAlias,
            onEdit: () => setIsOpenDrawerEditAlias(true),
            canCopy: true,
            canShare: isMobileNative,
            onCopy: () =>
                dispatch(
                    notificationActions.showNotification(i18nUtils.get("accounts.information.data.copied"), "success", [
                        "account/details",
                    ]),
                ),
            tooltipText: !isMobile ? i18nUtils.get("accounts.cbuAlias.edit.info") : "",
            tooltipPlacement: !isMobile && windowSize < 1060 ? "left" : "right",
        },
        {
            title: i18nUtils.get("accounts.information.headlines"),
            value: ownerName || " - ",
        },
        {
            title: i18nUtils.get(`documentType.label.${documentType}`),
            value: documentNumber ? formatDocument(documentNumber.toString(), documentType) : " - ",
        },
    ];

    const shareAccount = (e) => {
        e.preventDefault();

        const sharePayload = {
            header: activeEnvironment.name,
            cbu,
            accountType: shortLabel.substring(shortLabel.indexOf(productType)),
            alias: cbuAlias || "",
            cuitCuil: activeEnvironment.clients[0].idClient,
            footer: "global.companyName",
        };
        dispatch(accountsActions.shareAccount(sharePayload));
    };

    return (
        <>
            <Box px={isMobile ? 16 : 0} column gap={24}>
                <DataDisplay data={data} shadow="sm" withDividers />
                {isMobile && (
                    <Box w="100%" column gap={24} mb={24}>
                        {isMobileNative && (
                            <Button
                                icon="ShareAlt"
                                variant="outline"
                                iconVariant="outline"
                                text={i18nUtils.get("global.share")}
                                onClick={shareAccount}
                                w="100%"
                            />
                        )}
                        <Button
                            icon="SendDown"
                            variant="outline"
                            iconVariant="outline"
                            text={i18nUtils.get("global.download")}
                            onClick={() => setIsOpenDownloadOptionsMobile(true)}
                            w="100%"
                            isInactive={fetchingDownload}
                            loading={fetchingDownload}
                        />
                        <BottomSheet
                            isVisible={isOpenDownloadOptionsMobile}
                            onClose={() => setIsOpenDownloadOptionsMobile(false)}>
                            <Box
                                flex
                                align="center"
                                aria-label="PDF"
                                onClick={() => {
                                    onClickDownloadInfo("pdf");
                                    setIsOpenDownloadOptionsMobile(false);
                                }}
                                role="button">
                                <Box p={24} flex align="center" gap={12}>
                                    <Icon name="SendDown" variant="outline" color="text-color" size={16} />
                                    <Text variant="body" align="center">
                                        {i18nUtils.get(`global.download.pdf`)}
                                    </Text>
                                </Box>
                            </Box>
                            <Separator color="neutral-light" />
                            <Box
                                flex
                                align="center"
                                aria-label="XLS"
                                onClick={() => {
                                    onClickDownloadInfo("xls");
                                    setIsOpenDownloadOptionsMobile(false);
                                }}
                                role="button">
                                <Box p={24} flex align="center" gap={12}>
                                    <Icon name="SendDown" variant="outline" color="text-color" size={16} />
                                    <Text variant="body" align="center">
                                        {i18nUtils.get(`global.download.xls`)}
                                    </Text>
                                </Box>
                            </Box>
                            <Separator color="neutral-light" />
                            <Box
                                flex
                                align="center"
                                aria-label="CSV"
                                onClick={() => {
                                    onClickDownloadInfo("csv");
                                    setIsOpenDownloadOptionsMobile(false);
                                }}
                                role="button">
                                <Box p={24} flex align="center" gap={12}>
                                    <Icon name="SendDown" variant="outline" color="text-color" size={16} />
                                    <Text variant="body" align="center">
                                        {i18nUtils.get(`global.download.csv`)}
                                    </Text>
                                </Box>
                            </Box>
                        </BottomSheet>
                    </Box>
                )}
                {!isMobile && (
                    <Grid my={24}>
                        <Col size={{ xs: 3, xl: 2 }} offset={{ xs: 10, xl: 11 }}>
                            <Dropdown
                                w="100%"
                                direction="bottom"
                                optionWidth={buttonRef.current?.offsetWidth}
                                renderWithPortal={false}
                                label={
                                    <Button
                                        ref={buttonRef}
                                        icon="SendDown"
                                        variant="outline"
                                        iconVariant="outline"
                                        text={i18nUtils.get("global.download")}
                                        w="100%"
                                        type="button"
                                        isInactive={fetchingDownload}
                                        loading={fetchingDownload}
                                    />
                                }>
                                <DropdownItem>
                                    <button
                                        onClick={() => onClickDownloadInfo("pdf")}
                                        className="p-25 w-100 background-transparent border-none"
                                        type="button">
                                        <Text variant="body2" align="center">
                                            {i18nUtils.get(`global.download.pdf`)}
                                        </Text>
                                    </button>
                                </DropdownItem>
                                <DropdownItem>
                                    <button
                                        onClick={() => onClickDownloadInfo("xls")}
                                        className="p-25 w-100 background-transparent border-none"
                                        type="button">
                                        <Text variant="body2" align="center">
                                            {i18nUtils.get(`global.download.xls`)}
                                        </Text>
                                    </button>
                                </DropdownItem>
                                <DropdownItem>
                                    <button
                                        onClick={() => onClickDownloadInfo("csv")}
                                        className="p-25 w-100 background-transparent border-none"
                                        type="button">
                                        <Text variant="body2" align="center">
                                            {i18nUtils.get(`global.download.csv`)}
                                        </Text>
                                    </button>
                                </DropdownItem>
                            </Dropdown>
                        </Col>
                    </Grid>
                )}
            </Box>
            <ButtonDrawer
                showDrawerRemote={isOpenDrawerEditAlias}
                backButton={isMobile}
                closeRemotely={() => setIsOpenDrawerEditAlias(false)}
                buttonClassName="btn-link p-0 mx-2 medium-font f-size-6"
                width={!isMobile ? "600px" : "100%"}
                closeButtonWhite={isMobile}
                notificationContent={<Notification scopeToShow="account/alias" />}
                header={
                    isMobile && (
                        <div className="title-account-header-multiline-background-blue">
                            <div className="title-content mb-0">
                                <span>{productAlias || maskedAccountNumber(number)}</span>
                                <p className="my-0">
                                    {`${i18nUtils
                                        .get(`accounts.productType.${productTypes[productType]}`)
                                        .toUpperCase()}  Nº ${maskedAccountNumber(number)}`}
                                </p>
                            </div>
                        </div>
                    )
                }
                content={
                    <SetAlias
                        dispatch={dispatch}
                        closeDrawer={() => setIsOpenDrawerEditAlias(false)}
                        credentialGroups={credentialGroups}
                    />
                }
            />
        </>
    );
}

Information.propTypes = {
    dispatch: func.isRequired,
    account: shape({
        productType: string,
        currency: string,
        number: oneOfType([string, numberPropType]),
        cbu: string,
        cbuAlias: string,
        ownerName: string,
        documentNumber: oneOfType([string, numberPropType]),
        productAlias: string,
        shortLabel: string,
        documentType: string,
    }).isRequired,
    credentialGroups: arrayOf(shape({})),
    activeEnvironment: shape({
        name: string,
        clients: arrayOf(
            shape({
                idClient: string,
            }),
        ),
    }).isRequired,
    onClickDownloadInfo: func.isRequired,
    fetchingDownload: bool,
};

Information.defaultProps = {
    credentialGroups: [],
    fetchingDownload: false,
};

const mapStateToProps = (state) => ({
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    fetchingDownload: accountsSelectors.getFetchingDownload(state),
});

export default connect(mapStateToProps)(Information);
